import {
  Group,
  Text,
  Badge,
  ThemeIcon,
} from '@mantine/core';
import {
  FileText,
  Link as LinkIcon,
  File,
  Image,
  Film,
  Download,
  ExternalLink,
} from 'react-feather';
import type { CoachResource } from '../../../services/api/coachPortal';
import { formatFileSize } from './helpers';

/** Get icon for a resource based on type and mime type */
function getResourceIcon(resource: CoachResource) {
  if (resource.resource_type === 'link') return LinkIcon;
  if (resource.mime_type?.startsWith('image/')) return Image;
  if (resource.mime_type?.startsWith('video/')) return Film;
  if (resource.mime_type === 'application/pdf') return FileText;
  return File;
}

export interface ResourceRowProps {
  resource: CoachResource;
}

export function ResourceRow({ resource }: ResourceRowProps) {
  const Icon = getResourceIcon(resource);

  const handleAction = () => {
    if (resource.resource_type === 'link' && resource.url) {
      window.open(resource.url, '_blank', 'noopener,noreferrer');
    } else if (resource.download_url) {
      window.open(resource.download_url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Group
      justify="space-between"
      py="sm"
      px="md"
      style={(theme) => ({
        borderTop: `1px solid ${theme.colors.gray[2]}`,
      })}
    >
      <Group gap="sm" style={{ flex: 1, minWidth: 0 }}>
        <ThemeIcon size="md" variant="light" color="gray" radius="sm">
          <Icon size={16} />
        </ThemeIcon>
        <div style={{ minWidth: 0, flex: 1 }}>
          <Group gap="xs">
            <Text fw={500} size="sm" lineClamp={1}>
              {resource.title}
            </Text>
            {resource.is_evergreen && (
              <Badge size="xs" variant="light" color="teal">
                Evergreen
              </Badge>
            )}
          </Group>
          {resource.description && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {resource.description}
            </Text>
          )}
          {resource.resource_type === 'file' && resource.original_filename && (
            <Text size="xs" c="dimmed">
              {resource.original_filename}
              {resource.file_size ? ` (${formatFileSize(resource.file_size)})` : ''}
            </Text>
          )}
        </div>
      </Group>

      <Group gap="sm">
        {resource.category_name && (
          <Badge size="xs" variant="light" color="gray">
            {resource.category_name}
          </Badge>
        )}
        <ThemeIcon
          size="sm"
          variant="subtle"
          color="gray"
          style={{ cursor: 'pointer' }}
          onClick={handleAction}
        >
          {resource.resource_type === 'link' ? (
            <ExternalLink size={14} />
          ) : (
            <Download size={14} />
          )}
        </ThemeIcon>
      </Group>
    </Group>
  );
}
